/*!
 * MONTVIELLE — smart header engine (mv-header.js)
 * Loro Piana-style auto-hide header · mega menu · mobile drawer · predictive search · bag count
 *
 * State on <html>:
 *   data-mv-hdr="top|pinned|hidden"   scroll state, read by mv-header.css
 *   data-mv-hdr-mode="fixed"          only set if position:sticky can't travel in this theme's DOM
 *   --mv-ann-h / --mv-bar-h           measured announcement + bar heights
 *   --mv-hdr-offset                   px the header covers at the top of the viewport right now.
 *                                     Sticky UI elsewhere uses: top: var(--mv-hdr-offset)
 *
 * API:    window.MV.header.lock(reason) · unlock(reason) · reveal() · openSearch() · openMenu() · state
 * Events: document 'mv:header' → detail.state
 */
(() => {
  'use strict';
  if (window.MV && window.MV.header) return;

  const html = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const HIDE_AFTER_DOWN = 10;   // px of continuous downward travel before hiding
  const TOP_ZONE = 4;           // px from the top that still counts as "top"

  const locks = new Set();
  let ctx = null;
  let state = '';
  let lastY = 0;
  let travel = 0;
  let ticking = false;
  let scroller = null;          // null → the window scrolls; otherwise the element that does
  let stickyChecked = false;
  let annH = 0;
  let barH = 0;

  /* ------------------------------------------------------------------ scroll source */
  const mainEl = () => document.getElementById('MainContent') || document.querySelector('main');

  function findScroller() {
    const main = mainEl();
    for (let el = main && main.parentElement; el && el !== document.body; el = el.parentElement) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
    }
    return null;
  }
  const readY = () => (scroller ? scroller.scrollTop : window.scrollY);
  const maxY = () => (scroller
    ? scroller.scrollHeight - scroller.clientHeight
    : document.documentElement.scrollHeight - window.innerHeight);

  // One capture listener catches the window AND any scrolling wrapper; carousels/drawers are ignored.
  function onScroll(event) {
    const target = event.target;
    if (target === document) {
      scroller = null;
    } else {
      const main = mainEl();
      if (!(target instanceof Element) || !main || !target.contains(main)) return;
      scroller = target;
    }
    requestUpdate();
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  /* ------------------------------------------------------------------ state machine */
  function update() {
    ticking = false;
    if (!ctx) return;

    const y = Math.min(Math.max(readY(), 0), Math.max(0, maxY()));   // clamp iOS rubber-band overscroll
    const dy = y - lastY;
    lastY = y;

    // Never hide while locked, while the header still sits in its natural slot, or when disabled.
    if (ctx.static || locks.size || y <= annH + barH) {
      travel = 0;
      setState(y <= TOP_ZONE ? 'top' : 'pinned');
      return;
    }

    if (Math.sign(dy) !== Math.sign(travel)) travel = 0;   // direction flipped: restart the count
    travel += dy;

    if (travel > HIDE_AFTER_DOWN) setState('hidden');
    else if (travel < -ctx.revealDelta) setState('pinned');

    checkSticky(y);
  }

  function setState(next) {
    if (next === state) return;
    state = next;
    html.dataset.mvHdr = next;
    html.style.setProperty('--mv-hdr-offset', `${next === 'hidden' ? 0 : barH}px`);
    document.dispatchEvent(new CustomEvent('mv:header', { detail: { state: next } }));
  }

  // Safety net: if sticky can't travel (an ancestor clips it), switch to position:fixed once.
  function checkSticky(y) {
    if (stickyChecked || state !== 'pinned' || y < annH + barH + 200) return;
    const group = ctx.group;
    if (!group) return;
    stickyChecked = true;
    // Measure layout position, not the animated one: a reveal that is still sliding in from
    // translateY(-bar) would otherwise read as "scrolled away" and trigger a false fallback.
    const transform = getComputedStyle(group).transform;
    const shiftY = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m42 : 0;
    if (group.getBoundingClientRect().bottom - shiftY > barH / 2) return;
    html.dataset.mvHdrMode = 'fixed';
    const shift = annH + barH;   // CSS adds this as a spacer above <main>; keep the reader's place
    if (scroller) scroller.scrollTop += shift; else window.scrollBy(0, shift);
  }

  function lock(reason) { locks.add(reason); requestUpdate(); }
  function unlock(reason) { locks.delete(reason); requestUpdate(); }

  function measure() {
    if (!ctx) return;
    annH = ctx.ann ? ctx.ann.offsetHeight : 0;
    barH = ctx.bar.offsetHeight;
    html.style.setProperty('--mv-ann-h', `${annH}px`);
    html.style.setProperty('--mv-bar-h', `${barH}px`);
    html.style.setProperty('--mv-hdr-offset', `${state === 'hidden' ? 0 : barH}px`);
    // Horizon sections that size themselves against the header keep working
    document.body.style.setProperty('--header-height', `${barH}px`);
    document.body.style.setProperty('--header-group-height', `${annH + barH}px`);
  }

  function lockScroll(on) {
    html.classList.toggle('mv-lock', on);
    if (scroller) scroller.style.overflow = on ? 'hidden' : '';
  }

  /* ------------------------------------------------------------------ mega menu (desktop) */
  function bindMega(root, signal) {
    const items = [...root.querySelectorAll('.mv-nav__item[data-mv-mega]')];
    if (!items.length) return;
    let current = null;
    let openTimer = 0;
    let closeTimer = 0;

    const setOpen = (item, open) => {
      item.toggleAttribute('data-open', open);
      item.querySelector('.mv-nav__link').setAttribute('aria-expanded', String(open));
    };
    const open = (item) => {
      clearTimeout(closeTimer);
      if (current === item) return;
      if (current) {
        setOpen(current, false);
        ctx.hdr.setAttribute('data-mega-switch', '');   // panel → panel reads as one surface, no re-animation
      } else {
        ctx.hdr.setAttribute('data-mega-open', '');
        ctx.hdr.removeAttribute('data-mega-switch');
        lock('mega');
      }
      current = item;
      setOpen(item, true);
    };
    const close = () => {
      clearTimeout(openTimer);
      if (!current) return;
      setOpen(current, false);
      current = null;
      ctx.hdr.removeAttribute('data-mega-open');
      ctx.hdr.removeAttribute('data-mega-switch');
      unlock('mega');
    };

    items.forEach((item) => {
      const link = item.querySelector('.mv-nav__link');
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(() => open(item), current ? 0 : 90);   // hover intent
      }, { signal });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(close, 180);
      }, { signal });
      // Touch/tablet: first tap opens the panel, second tap follows the link
      link.addEventListener('click', (e) => {
        if (!finePointer.matches && current !== item) { e.preventDefault(); open(item); }
      }, { signal });
      link.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowDown' && e.key !== ' ') return;
        e.preventDefault();
        open(item);
        requestAnimationFrame(() => item.querySelector('.mv-mega a')?.focus());
      }, { signal });
      item.addEventListener('focusout', (e) => {
        if (current === item && !item.contains(e.relatedTarget)) close();
      }, { signal });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !current) return;
      const link = current.querySelector('.mv-nav__link');
      close();
      link.focus();
    }, { signal });
    document.addEventListener('pointerdown', (e) => {
      if (current && !current.contains(e.target)) close();
    }, { signal });

    ctx.closeMega = close;
  }

  /* ------------------------------------------------------------------ dialogs: menu drawer + search */
  function bindDialogs(root, signal) {
    const dialogs = [...root.querySelectorAll('dialog[data-mv-dialog]')];
    const setExpanded = (id, value) => root
      .querySelectorAll(`[data-mv-open][aria-controls="${id}"]`)
      .forEach((el) => el.setAttribute('aria-expanded', String(value)));

    const openDialog = (id, opener) => {
      const dialog = document.getElementById(id);
      if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return false;
      if (ctx.closeMega) ctx.closeMega();
      dialogs.forEach((d) => { if (d.open && d !== dialog) d.close(); });
      dialog.mvOpener = opener || document.activeElement;
      dialog.showModal();
      setExpanded(id, true);
      lock(`dialog:${id}`);
      lockScroll(true);
      return true;
    };

    dialogs.forEach((dialog) => {
      dialog.addEventListener('close', () => {
        setExpanded(dialog.id, false);
        unlock(`dialog:${dialog.id}`);
        const anotherOpen = dialogs.some((d) => d.open);
        lockScroll(anotherOpen);
        if (!anotherOpen && dialog.mvOpener && dialog.mvOpener.focus) dialog.mvOpener.focus({ preventScroll: true });
        setTimeout(() => resetPanes(dialog), 450);
      }, { signal });
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); }, { signal });   // backdrop
      dialog.addEventListener('cancel', (e) => {                                                          // Esc steps back a level first
        const active = dialog.querySelector('[data-mv-pane][data-active]');
        if (active) { e.preventDefault(); popPane(active); }
      }, { signal });
    });

    root.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-mv-open]');
      if (opener) {
        if (openDialog(opener.getAttribute('aria-controls'), opener)) e.preventDefault();
        return;
      }
      const closer = e.target.closest('[data-mv-close]');
      if (closer) { closer.closest('dialog').close(); return; }
      const push = e.target.closest('[data-mv-push]');
      if (push) { pushPane(push); return; }
      const pop = e.target.closest('[data-mv-pop]');
      if (pop) popPane(pop.closest('[data-mv-pane]'));
    }, { signal });

    ctx.openDialog = openDialog;
  }

  function pushPane(trigger) {
    const pane = document.getElementById(trigger.getAttribute('aria-controls'));
    const rootPane = trigger.closest('[data-mv-pane="root"]');
    if (!pane || !rootPane) return;
    rootPane.setAttribute('data-pushed', '');
    pane.setAttribute('data-active', '');
    pane.scrollTop = 0;
    trigger.setAttribute('aria-expanded', 'true');
    setTimeout(() => pane.querySelector('[data-mv-pop]')?.focus({ preventScroll: true }), 60);
  }

  function popPane(pane) {
    if (!pane) return;
    const rootPane = pane.parentElement.querySelector('[data-mv-pane="root"]');
    const trigger = rootPane && rootPane.querySelector(`[aria-controls="${pane.id}"]`);
    pane.removeAttribute('data-active');
    if (rootPane) rootPane.removeAttribute('data-pushed');
    if (trigger) { trigger.setAttribute('aria-expanded', 'false'); trigger.focus({ preventScroll: true }); }
  }

  function resetPanes(dialog) {
    if (dialog.open) return;
    dialog.querySelectorAll('[data-active]').forEach((el) => el.removeAttribute('data-active'));
    dialog.querySelectorAll('[data-pushed]').forEach((el) => el.removeAttribute('data-pushed'));
    dialog.querySelectorAll('[data-mv-push]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
  }

  /* ------------------------------------------------------------------ announcement rotation */
  function bindAnnouncements(root, signal) {
    const bar = root.querySelector('[data-mv-ann]');
    if (!bar) return;
    const messages = [...bar.querySelectorAll('.mv-ann__msg')];
    if (messages.length < 2 || reduceMotion.matches) return;
    let index = 0;
    let paused = false;
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    ['pointerenter', 'focusin'].forEach((t) => bar.addEventListener(t, pause, { signal }));
    ['pointerleave', 'focusout'].forEach((t) => bar.addEventListener(t, resume, { signal }));
    const timer = setInterval(() => {
      if (paused || document.hidden) return;
      messages[index].classList.remove('is-active');
      index = (index + 1) % messages.length;
      messages[index].classList.add('is-active');
    }, (Number(bar.dataset.speed) || 5) * 1000);
    signal.addEventListener('abort', () => clearInterval(timer));
  }

  /* ------------------------------------------------------------------ predictive search (Section Rendering API) */
  function bindSearch(root, signal) {
    const dialog = root.querySelector('#MvSearch');
    if (!dialog) return;
    const input = dialog.querySelector('[data-mv-search-input]');
    const results = dialog.querySelector('[data-mv-search-results]');
    const popular = dialog.querySelector('[data-mv-search-popular]');
    const endpoint = root.dataset.psUrl;
    const cache = new Map();
    let timer = 0;
    let controller = null;

    const render = (markup) => {
      const node = markup ? new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-mv-ps]') : null;
      results.replaceChildren(...(node ? [node] : []));
      if (popular) popular.hidden = Boolean(node);
    };

    const run = async () => {
      const q = input.value.trim();
      if (controller) controller.abort();
      if (q.length < 2) return render(null);
      if (cache.has(q)) return render(cache.get(q));
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'mv-predictive-search',
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });
      results.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${endpoint}?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const markup = await response.text();
        cache.set(q, markup);
        render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') render(null);
      } finally {
        results.removeAttribute('aria-busy');
      }
    };

    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(run, 180); }, { signal });
  }

  /* ------------------------------------------------------------------ bag count */
  function bindCart(root, signal) {
    const counts = root.querySelectorAll('[data-mv-cart-count]');
    const bag = root.querySelector('[data-mv-cart-trigger]');
    const set = (n) => {
      counts.forEach((el) => { el.textContent = n; el.hidden = !n; });
      if (bag) bag.setAttribute('aria-label', `Bag, ${n} ${n === 1 ? 'item' : 'items'}`);
    };
    const refresh = () => fetch(`${root.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => set(cart.item_count))
      .catch(() => {});

    ['cart:update', 'cart:updated', 'mv:cart:changed'].forEach((name) => {
      document.addEventListener(name, (e) => {
        const d = e.detail || {};
        const n = d.item_count ?? d.cart?.item_count ?? d.resource?.item_count;
        if (typeof n === 'number') set(n); else refresh();
      }, { signal });
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); }, { signal });
    // The cart drawer (Phase 3.2) registers window.MV.cart.open; until then the bag links to /cart.
    if (bag) bag.addEventListener('click', (e) => {
      if (window.MV.cart && typeof window.MV.cart.open === 'function') { e.preventDefault(); window.MV.cart.open(); }
    }, { signal });
  }

  /* ------------------------------------------------------------------ init / theme editor */
  function init(root) {
    if (!root) return;
    if (ctx) { ctx.controller.abort(); ctx.resizeObserver?.disconnect(); }
    locks.clear();

    const controller = new AbortController();
    const signal = controller.signal;
    ctx = {
      root,
      controller,
      group: root.closest('#header-group') || root.closest('.shopify-section'),
      hdr: root.querySelector('.mv-hdr'),
      ann: root.querySelector('.mv-ann'),
      bar: root.querySelector('.mv-bar'),
      static: root.dataset.hideOnScroll === 'false',
      revealDelta: Number(root.dataset.revealDelta) || 6,
    };

    measure();
    if ('ResizeObserver' in window) {
      ctx.resizeObserver = new ResizeObserver(() => { measure(); requestUpdate(); });
      [ctx.ann, ctx.bar].forEach((el) => el && ctx.resizeObserver.observe(el));
    }

    // Keyboard users always see where focus is
    ctx.hdr.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) lock('focus'); }, { signal });
    ctx.hdr.addEventListener('focusout', (e) => { if (!ctx.hdr.contains(e.relatedTarget)) unlock('focus'); }, { signal });

    bindMega(root, signal);
    bindDialogs(root, signal);
    bindAnnouncements(root, signal);
    bindSearch(root, signal);
    bindCart(root, signal);

    scroller = findScroller();
    lastY = readY();
    state = '';
    update();
  }

  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  // Desktop nicety: nudging the pointer to the very top edge brings a hidden header back
  document.addEventListener('pointermove', (e) => {
    if (state === 'hidden' && e.pointerType === 'mouse' && e.clientY < 12) setState('pinned');
  }, { passive: true });
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-mv-header]');
    if (root) init(root);
  });

  window.MV = window.MV || {};
  window.MV.header = {
    lock,
    unlock,
    reveal: () => setState(lastY <= TOP_ZONE ? 'top' : 'pinned'),
    openSearch: () => ctx && ctx.openDialog && ctx.openDialog('MvSearch'),
    openMenu: () => ctx && ctx.openDialog && ctx.openDialog('MvNav'),
    get state() { return state; },
  };

  const boot = () => init(document.querySelector('[data-mv-header]'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
